import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import VictoryModal from '../../components/game/VictoryModal';
import HowToPlayModal from '../../components/game/HowToPlayModal';
import {
  generateBricksPuzzle,
  validateBricksSolution,
  type BricksLevel,
} from '../../services/bricksService';
import { useGameProgress } from '../../hooks/useGameProgress';
import { useSessionContext } from '../../hooks/useSessionContext';
import SessionBanner from '../../components/game/SessionBanner';

interface HistoryEntry {
  row: number;
  col: number;
  anterior: string;
  nuevo: string;
}

export default function BricksGame() {
  const [searchParams, setSearchParams] = useSearchParams();

  const dificultad_id = parseInt(searchParams.get('dificultad') || '1');
  const seedParam = searchParams.get('seed') ? parseInt(searchParams.get('seed')!) : undefined;

  const [level, setLevel] = useState<BricksLevel | null>(null);
  const [userBoard, setUserBoard] = useState<string[][]>([]);
  const [initialBoard, setInitialBoard] = useState<string[][]>([]);
  const [startTime] = useState(Date.now());
  const [elapsedTime, setElapsedTime] = useState(0);
  const [solved, setSolved] = useState(false);
  const [victoryModalOpen, setVictoryModalOpen] = useState(false);
  const [showingSolution, setShowingSolution] = useState(false);
  const [showHowToPlay, setShowHowToPlay] = useState(false);
  const [modoAsistido, setModoAsistido] = useState(false);
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [selectedCell, setSelectedCell] = useState<{ row: number; col: number } | null>(null);

  // El tablero se dimensiona midiendo su hueco real. Restar una altura fija a
  // 100vh no sirve: la cabecera se parte en mas lineas segun el ancho y el pad
  // cambia de sitio en pantalla ancha, asi que el hueco disponible varia.
  const areaRef = useRef<HTMLDivElement>(null);
  const padRef = useRef<HTMLDivElement>(null);
  const [boardBox, setBoardBox] = useState({ w: 0, h: 0 });

  useEffect(() => {
    const area = areaRef.current;
    if (!area) return;

    const medir = () => {
      const pad = padRef.current;
      // En ancho el pad va en columna al lado y descuenta ancho; en estrecho es
      // una barra abajo y descuenta alto. GAP son los 12px de lg:gap-3.
      const enColumna = window.matchMedia('(min-width: 1024px)').matches;
      const GAP = enColumna ? 12 : 0;
      setBoardBox({
        w: area.clientWidth - (enColumna && pad ? pad.offsetWidth + GAP : 0),
        h: area.clientHeight - (!enColumna && pad ? pad.offsetHeight : 0),
      });
    };

    medir();
    const ro = new ResizeObserver(medir);
    ro.observe(area);
    if (padRef.current) ro.observe(padRef.current);
    return () => ro.disconnect();
  }, [level]);

  const difficulties = [
    { orden: 1, nombre: 'Neurona',  icono: '🧠', color: '#22c55e' },
    { orden: 2, nombre: 'Sinapsis', icono: '⚡', color: '#3b82f6' },
    { orden: 3, nombre: 'Cerebro',  icono: '🧩', color: '#a855f7' },
    { orden: 4, nombre: 'Genio',    icono: '💡', color: '#ef4444' },
    { orden: 5, nombre: 'Einstein', icono: '🎓', color: '#f97316' },
  ];

  const dificultad_actual = difficulties.find(d => d.orden === dificultad_id) || difficulties[0];

  const { saveProgress: saveGameProgress } = useGameProgress('bricks', dificultad_id);
  const { isInSession, sessionType, saveSessionResult } = useSessionContext();

  useEffect(() => { loadPuzzle(seedParam); }, [dificultad_id]);

  useEffect(() => {
    if (solved) return;
    const timer = setInterval(() => setElapsedTime(Math.floor((Date.now() - startTime) / 1000)), 1000);
    return () => clearInterval(timer);
  }, [solved, startTime]);

  // Teclado fisico: el juego se usa sobre todo en desktop durante la sesion,
  // asi que las celdas ya no son <input> pero se sigue escribiendo igual.
  useEffect(() => {
    if (solved || !level) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (!selectedCell) return;
      const { row, col } = selectedCell;
      const size = level.size;

      if (e.key === 'Escape') { setSelectedCell(null); return; }

      const moves: Record<string, [number, number]> = {
        ArrowUp: [-1, 0], ArrowDown: [1, 0], ArrowLeft: [0, -1], ArrowRight: [0, 1],
      };
      if (moves[e.key]) {
        e.preventDefault();
        const [dr, dc] = moves[e.key];
        let nr = row + dr;
        let nc = col + dc;
        // Avanza hasta la siguiente celda editable; las fijas se saltan
        while (nr >= 0 && nr < size && nc >= 0 && nc < size && initialBoard[nr][nc]) {
          nr += dr;
          nc += dc;
        }
        if (nr >= 0 && nr < size && nc >= 0 && nc < size) setSelectedCell({ row: nr, col: nc });
        return;
      }

      if (e.key === 'Backspace' || e.key === 'Delete' || e.key === '0') {
        e.preventDefault();
        handleInput(row, col, '');
        return;
      }

      if (/^[1-9]$/.test(e.key) && parseInt(e.key) <= size) {
        e.preventDefault();
        handleInput(row, col, e.key);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [selectedCell, userBoard, initialBoard, level, solved, history, historyIndex]);

  async function loadPuzzle(seed?: number) {
    try {
      const puzzle = await generateBricksPuzzle(dificultad_id, seed);
      setLevel(puzzle);
      setSearchParams(p => { p.set('seed', String(puzzle.id)); return p; }, { replace: true });
      initializeBoard(puzzle);
      setSolved(false);
      setVictoryModalOpen(false);
      setShowingSolution(false);
      setHistory([]);
      setHistoryIndex(-1);
      setSelectedCell(null);
    } catch {
      toast.error('Error cargando el puzzle');
    }
  }

  function initializeBoard(lvl: BricksLevel) {
    const size = lvl.size;
    const board: string[][] = Array(size).fill(null).map(() => Array(size).fill(''));
    const initial: string[][] = Array(size).fill(null).map(() => Array(size).fill(''));
    lvl.problem.forEach((row, i) => {
      row.split(' ').forEach((cell, j) => {
        if (cell && cell !== '-') { board[i][j] = cell; initial[i][j] = cell; }
      });
    });
    setUserBoard(board);
    setInitialBoard(initial);
  }

  function handleInput(row: number, col: number, value: string) {
    if (solved || !level) return;
    if (initialBoard[row][col]) return;
    if (value && (isNaN(parseInt(value)) || parseInt(value) < 1 || parseInt(value) > level.size)) return;

    const anterior = userBoard[row][col];
    if (anterior === value) return;

    const newHistory = [...history.slice(0, historyIndex + 1), { row, col, anterior, nuevo: value }];
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);

    const newBoard = userBoard.map(r => [...r]);
    newBoard[row][col] = value;
    setUserBoard(newBoard);

    if (isBoardComplete(newBoard)) setTimeout(() => checkSolution(newBoard), 300);
  }

  function isBoardComplete(board: string[][]) {
    return board.every(row => row.every(cell => cell !== ''));
  }

  async function checkSolution(board: string[][]) {
    if (solved || !level) return;
    let validation;
    try {
      validation = await validateBricksSolution(board, level.solution.map(r => r.split(' ')), level.areas);
    } catch {
      toast.error('Error validando la solución');
      return;
    }

    if (validation.valid) {
      setSolved(true);
      const tiempo = Math.floor((Date.now() - startTime) / 1000);
      setVictoryModalOpen(true);
      try {
        await saveGameProgress({ tablero_id: level.id, tiempo, movimientos: history.length });
        await saveSessionResult({ tiempo, movimientos: history.length, completado: true,
          datos_juego: { tablero_id: level.id, size: level.size } });
      } catch {
        console.warn('Error guardando progreso de Bricks');
      }
    } else {
      toast.error('Solución incorrecta. Revisa los números.');
    }
  }

  function undo() {
    if (historyIndex < 0 || solved) return;
    const move = history[historyIndex];
    const newBoard = userBoard.map(r => [...r]);
    newBoard[move.row][move.col] = move.anterior;
    setUserBoard(newBoard);
    setHistoryIndex(historyIndex - 1);
  }

  function redo() {
    if (historyIndex >= history.length - 1 || solved) return;
    const move = history[historyIndex + 1];
    const newBoard = userBoard.map(r => [...r]);
    newBoard[move.row][move.col] = move.nuevo;
    setUserBoard(newBoard);
    setHistoryIndex(historyIndex + 1);
  }

  function reset() {
    if (!level) return;
    initializeBoard(level);
    setSolved(false);
    setShowingSolution(false);
    setHistory([]);
    setHistoryIndex(-1);
    setSelectedCell(null);
  }

  function handleHint() {
    if (!level || solved) return;
    const sol = level.solution.map(r => r.split(' '));

    // Primero corregir un error
    for (let r = 0; r < level.size; r++) {
      for (let c = 0; c < level.size; c++) {
        if (initialBoard[r][c]) continue;
        if (userBoard[r][c] && userBoard[r][c] !== sol[r][c]) {
          const newBoard = userBoard.map(row => [...row]);
          newBoard[r][c] = sol[r][c];
          setUserBoard(newBoard);
          return;
        }
      }
    }

    // Luego revelar una celda vacía
    const empty: [number, number][] = [];
    for (let r = 0; r < level.size; r++)
      for (let c = 0; c < level.size; c++)
        if (!userBoard[r][c] && !initialBoard[r][c]) empty.push([r, c]);

    if (empty.length === 0) { toast.info('No hay más pistas disponibles'); return; }

    const [r, c] = empty[Math.floor(Math.random() * empty.length)];
    const newBoard = userBoard.map(row => [...row]);
    newBoard[r][c] = sol[r][c];
    const newHistory = [...history.slice(0, historyIndex + 1), { row: r, col: c, anterior: '', nuevo: sol[r][c] }];
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
    setUserBoard(newBoard);
  }

  function toggleSolution() {
    if (!level) return;
    if (!showingSolution) {
      if (!window.confirm('¿Estás seguro de que quieres ver la solución? Esto no guardará tu progreso.')) return;
      const sol = level.solution.map(r => r.split(' '));
      setUserBoard(sol);
      setShowingSolution(true);
    } else {
      reset();
    }
  }

  /**
   * Papel de la celda dentro de su ladrillo: mitad izquierda, mitad derecha, o
   * pieza suelta (las puntas de las filas trabadas). De aqui salen tanto los
   * bordes como el relieve, para que las dos mitades se lean como UNA pieza.
   */
  type BrickRole = 'left' | 'right' | 'single';

  function getBrickRole(row: number, col: number): BrickRole {
    if (!level) return 'single';
    const { areas, size } = level;
    if (areas === 'single') return col % 2 === 0 ? 'left' : 'right';
    // double y double-wrapped traban las filas impares
    const offset = row % 2 === 0 ? 0 : 1;
    if (offset === 1 && (col === 0 || col === size - 1)) return 'single';
    return (col - offset) % 2 === 0 ? 'left' : 'right';
  }

  function getBrickClasses(row: number, col: number): string {
    const base = 'brick-border-top brick-border-bottom';
    switch (getBrickRole(row, col)) {
      // La junta interior es tenue y sin mortero: las dos mitades son una pieza
      case 'left':  return `${base} brick-border-left brick-inner-right`;
      case 'right': return `${base} brick-border-right brick-inner-left`;
      default:      return `${base} brick-border-left brick-border-right`;
    }
  }

  /**
   * Relieve del ladrillo, no de la celda: la mitad izquierda no lleva sombra a
   * su derecha y la derecha no lleva luz a su izquierda, asi el bisel rodea la
   * pareja entera en vez de dibujar dos piezas pegadas.
   */
  function getBrickBevel(row: number, col: number, luz: number, sombra: number): string {
    const L = `rgba(255,255,255,${luz})`;
    const S = `rgba(0,0,0,${sombra})`;
    switch (getBrickRole(row, col)) {
      case 'left':  return `inset 1px 1px 3px ${L}, inset 0 -1px 3px ${S}`;
      case 'right': return `inset 0 1px 3px ${L}, inset -1px -1px 3px ${S}`;
      default:      return `inset 1px 1px 3px ${L}, inset -1px -1px 3px ${S}`;
    }
  }

  function calcularErrores(board: string[][]): Set<string> {
    const errores = new Set<string>();
    if (!level) return errores;
    const size = level.size;

    for (let r = 0; r < size; r++) {
      const seen = new Map<string, number>();
      for (let c = 0; c < size; c++) {
        const v = board[r][c]; if (!v) continue;
        if (seen.has(v)) { errores.add(`${r},${seen.get(v)!}`); errores.add(`${r},${c}`); }
        else seen.set(v, c);
      }
    }
    for (let c = 0; c < size; c++) {
      const seen = new Map<string, number>();
      for (let r = 0; r < size; r++) {
        const v = board[r][c]; if (!v) continue;
        if (seen.has(v)) { errores.add(`${seen.get(v)!},${c}`); errores.add(`${r},${c}`); }
        else seen.set(v, r);
      }
    }
    const getPairs = () => {
      const pairs: [number,number][][] = [];
      const areas = level.areas;
      if (areas === 'single') {
        for (let r = 0; r < size; r++) for (let c = 0; c < size-1; c += 2) pairs.push([[r,c],[r,c+1]]);
      } else {
        for (let r = 0; r < size; r++) {
          const off = r%2===0?0:1;
          for (let c=off; c<size-1; c+=2) pairs.push([[r,c],[r,c+1]]);
          if (areas === 'double-wrapped' && r%2===1) pairs.push([[r,size-1],[r,0]]);
        }
      }
      return pairs;
    };
    for (const pair of getPairs()) {
      const vals = pair.map(([r,c]) => board[r][c]);
      if (vals.some(v => !v)) continue;
      if (Number(vals[0]) % 2 === Number(vals[1]) % 2) pair.forEach(([r,c]) => errores.add(`${r},${c}`));
    }
    return errores;
  }

  const formatTime = (s: number) => `${String(Math.floor(s/60)).padStart(2,'0')}:${String(s%60).padStart(2,'0')}`;

  if (!level) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#667eea] to-[#764ba2] flex items-center justify-center">
        <div className="text-white text-2xl">Cargando...</div>
      </div>
    );
  }

  // La celda puede ensancharse hasta 1.4:1, no mas: a 3:1 medio ladrillo ya
  // tenia la forma canonica de uno entero (2:1) y no habia quien los
  // distinguiese. Con el tope, el entero sigue siendo el doble de ancho que el
  // medio. El alto disponible cambia segun el pad este abajo o en el lateral.
  // Tope de 1.4:1: a 3:1 medio ladrillo ya tenia la forma canonica de uno
  // entero (2:1) y no habia quien los distinguiese. Con el tope, un ladrillo
  // entero sigue siendo el doble de ancho que un medio.
  const MARGEN = 16;   // aire alrededor del tablero, incluido el p-1 del hueco
  const dispW = boardBox.w > 0 ? (boardBox.w - MARGEN) / level.size : 40;
  const dispH = boardBox.h > 0 ? (boardBox.h - MARGEN) / level.size : 40;
  // La celda se queda siempre entre 1.0:1 y 1.4:1. Nunca mas alta que ancha,
  // porque un ladrillo es horizontal; y nunca mas de 1.4 veces mas ancha,
  // porque a 3:1 medio ladrillo ya tiene la forma de uno entero y no hay quien
  // los distinga. El alto lo limita tambien el ancho disponible: en movil
  // sobra alto y sin ese tope las celdas salian verticales.
  const cellH = Math.floor(Math.min(dispH, dispW));
  const cellW = Math.floor(Math.min(dispW, cellH * 1.4));
  const fontSize = `${Math.floor(Math.min(cellW, cellH) * 0.45)}px`;
  const padEnabled = selectedCell !== null && !solved && !initialBoard[selectedCell.row]?.[selectedCell.col];

  return (
    <div className="h-full overflow-hidden bg-gradient-to-br from-[#667eea] to-[#764ba2] flex flex-col">
      <style>{`
        .brick-border-top    { border-top:    4px solid rgba(0,0,0,0.8) !important; }
        .brick-border-bottom { border-bottom: 4px solid rgba(0,0,0,0.8) !important; }
        .brick-border-left   { border-left:   4px solid rgba(0,0,0,0.8) !important; }
        .brick-border-right  { border-right:  4px solid rgba(0,0,0,0.8) !important; }
        .brick-inner-right   { border-right:  1px solid rgba(0,0,0,0.15) !important; }
        .brick-inner-left    { border-left:   0 !important; }

      `}</style>

      {isInSession && sessionType && <SessionBanner sessionType={sessionType as 'presencial' | 'remota'} />}

      {/* Top bar */}
      <div className="bg-black/20 backdrop-blur-sm px-3 py-2 flex items-center gap-2 flex-wrap">
        <span className="text-white font-bold text-base whitespace-nowrap">🧱 Ladrillos</span>
        <div className="w-px h-5 bg-white/30 hidden sm:block" />
        <div className="flex gap-1 flex-wrap">
          {difficulties.map(d => (
            <button key={d.orden}
              onClick={() => setSearchParams({ dificultad: String(d.orden) })}
              className={`px-2 py-0.5 rounded text-xs font-semibold transition-all ${dificultad_id === d.orden ? 'bg-white text-purple-700' : 'bg-white/20 text-white hover:bg-white/30'}`}
            >{d.icono} {d.nombre}</button>
          ))}
        </div>
        <div className="flex-1" />
        <div className="flex gap-1 items-center">
          <button onClick={handleHint} disabled={solved} title="Pista"
            className="p-1.5 rounded bg-white/20 hover:bg-white/30 text-white transition-all disabled:opacity-40">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m1.343-5.657l-.707-.707M12 21v-1M5.636 5.636l.707.707M12 13a3 3 0 100-6 3 3 0 000 6z"/></svg>
          </button>
          <button onClick={toggleSolution} title={showingSolution ? 'Ocultar solución' : 'Ver solución'}
            className="p-1.5 rounded bg-white/20 hover:bg-white/30 text-white transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/><path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/></svg>
          </button>
          <button onClick={undo} disabled={historyIndex < 0 || solved} title="Deshacer"
            className="p-1.5 rounded bg-white/20 hover:bg-white/30 text-white transition-all disabled:opacity-40">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"/></svg>
          </button>
          <button onClick={redo} disabled={historyIndex >= history.length - 1 || solved} title="Rehacer"
            className="p-1.5 rounded bg-white/20 hover:bg-white/30 text-white transition-all disabled:opacity-40">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M21 10H11a8 8 0 00-8 8v2M21 10l-6 6m6-6l-6-6"/></svg>
          </button>
          <button onClick={() => setModoAsistido(v => !v)} title={modoAsistido ? 'Desactivar modo asistido' : 'Activar modo asistido'}
            className={`p-1.5 rounded transition-all text-white ${modoAsistido ? 'bg-blue-500 ring-2 ring-blue-300' : 'bg-white/20 hover:bg-white/30'}`}>
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
          </button>
          <button onClick={reset} title="Reiniciar"
            className="p-1.5 rounded bg-white/20 hover:bg-white/30 text-white transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/></svg>
          </button>
          <button onClick={() => loadPuzzle()} title="Nuevo puzzle"
            className="p-1.5 rounded bg-white/20 hover:bg-white/30 text-white transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4"/></svg>
          </button>
          <button onClick={() => setShowHowToPlay(true)} title="Cómo se juega"
            className="p-1.5 rounded bg-white/20 hover:bg-white/30 text-white transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
          </button>
        </div>
      </div>

      {/* Stats bar */}
      <div className="bg-black/10 px-3 py-1 flex items-center gap-4 text-white/80 text-xs">
        <span>{dificultad_actual.icono} {dificultad_actual.nombre} — Puzzle #{level.id}</span>
        <span>⏱ {formatTime(elapsedTime)}</span>
      </div>

      {/* Board */}
      <div ref={areaRef} className="flex-1 min-h-0 overflow-hidden flex flex-col lg:flex-row lg:items-center lg:justify-center lg:gap-3">
        <div className="flex-1 lg:flex-none min-h-0 p-1 flex items-center justify-center">
        <div className="grid gap-0 border-[3px] border-[#4a2208]"
          style={{
            gridTemplateColumns: `repeat(${level.size}, ${cellW}px)`,
            gridTemplateRows: `repeat(${level.size}, ${cellH}px)`,
            backgroundColor: '#d4b896',
          }}>
          {(() => {
            const errores = modoAsistido ? calcularErrores(userBoard) : new Set<string>();
            return userBoard.map((row, i) => row.map((cell, j) => {
              const isFixed = initialBoard[i][j] !== '';
              const hasError = errores.has(`${i},${j}`);
              const cellBg = hasError
                ? { background: 'linear-gradient(145deg, #e84040, #cc2020)', boxShadow: getBrickBevel(i, j, 0.15, 0.4) }
                : isFixed
                ? { background: 'linear-gradient(145deg, #a84020, #923010, #7a2808)', boxShadow: `inset 0 0 0 3px rgba(251,191,36,0.85), ${getBrickBevel(i, j, 0.15, 0.45)}` }
                : { background: 'linear-gradient(145deg, #c86840, #b04020, #983518)', boxShadow: getBrickBevel(i, j, 0.2, 0.4) };
              const isSelected = !isFixed && selectedCell?.row === i && selectedCell?.col === j;
              return (
                <div key={`${i}-${j}`}
                  onClick={() => { if (!isFixed && !solved) setSelectedCell({ row: i, col: j }); }}
                  className={`flex items-center justify-center border border-[#d4b896] ${getBrickClasses(i, j)} ${!isFixed && !solved ? 'cursor-pointer' : ''}`}
                  style={{
                    ...cellBg,
                    ...(isSelected ? { outline: '3px solid #fbbf24', outlineOffset: '-3px' } : {}),
                  }}>
                  <span
                    className={`font-bold ${isFixed ? 'text-white' : showingSolution ? 'text-yellow-200' : 'text-white/90'}`}
                    style={{ fontSize, textShadow: '0 1px 2px rgba(0,0,0,0.6)' }}
                  >
                    {cell}
                  </span>
                </div>
              );
            }));
          })()}
        </div>
      </div>

      {/* Pad numérico virtual: mismo patrón que Fillomino / Suguru / Futoshiki / Torres */}
      {level && (
        <div ref={padRef} className={`bg-black/20 backdrop-blur-sm px-3 pt-2 pb-safe pb-safe-off-lg flex items-center justify-center gap-2 flex-wrap lg:flex-col lg:flex-nowrap lg:rounded-xl lg:py-3 lg:self-center transition-opacity ${padEnabled ? '' : 'opacity-60 pointer-events-none'}`}>
          {Array.from({ length: level.size }, (_, i) => i + 1).map(num => (
            <button key={num} onClick={() => { if (padEnabled) handleInput(selectedCell!.row, selectedCell!.col, String(num)); }} disabled={!padEnabled}
              className="w-11 h-11 flex items-center justify-center bg-gradient-to-br from-[#c86840] to-[#983518] hover:from-[#d87850] hover:to-[#a84020] text-white font-bold rounded-lg shadow active:scale-95 transition-all text-lg">
              {num}
            </button>
          ))}
          <button onClick={() => { if (padEnabled) handleInput(selectedCell!.row, selectedCell!.col, ''); }} disabled={!padEnabled} title="Borrar"
            className="w-11 h-11 ml-2 lg:ml-0 lg:mt-2 flex items-center justify-center bg-red-500 hover:bg-red-600 text-white font-bold rounded-lg shadow active:scale-95 transition-all text-base">
            ✕
          </button>
        </div>
      )}
      </div>

      <HowToPlayModal isOpen={showHowToPlay} onClose={() => setShowHowToPlay(false)}
        gameTitle="Ladrillos" gameIcon="🧱"
        objective="Completa el tablero colocando los números del 1 al N sin repetir en filas ni columnas, y con par e impar en cada ladrillo. Las dos medias casillas de los extremos de una misma fila cuentan como un ladrillo."
        rules={[
          'Coloca los números del 1 al N en cada fila y columna sin repetir ninguno',
          'El tablero está dividido en ladrillos de 2×1 marcados con bordes gruesos',
          'Cada ladrillo debe contener exactamente un número par y uno impar',
          'En las filas trabadas, la media casilla del extremo izquierdo y la del extremo derecho forman un único ladrillo: entre esas dos casillas también tiene que haber un par y un impar',
        ]}
        howToPlay="Toca una celda vacía para seleccionarla (se resalta en amarillo) y elige un número en el pad de abajo, o escríbelo con el teclado. Usa ✕ o Retroceso para borrar, y las flechas para moverte entre celdas. Los números fijos no se pueden cambiar."
        author="Otto Janko" />

      <VictoryModal isOpen={victoryModalOpen} onClose={() => setVictoryModalOpen(false)}
        stats={{ tiempo: Math.floor((Date.now() - startTime) / 1000) }} />
    </div>
  );
}
